import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames, { Argument } from 'classnames';

import Media, { MediaInterface } from 'types/Media';
import { AbstractMedia, MediaType, RenderOptions } from 'types/AbstractMedia';

import Icon from './Icon';
import Image from './Image';
import Video from './Video';
import Spinner from './Spinner';
import Document from './Document';
import Button, { ButtonType } from './Button';

export const DEFAULT_MEDIA_COMPONENTS: Record<MediaType, (
  media: AbstractMedia,
  renderOptions?: RenderOptions,
) => React.ReactElement> = {
  [MediaType.IMAGE]: (
    { path, thumbnailPath, ...image },
    { className, thumbnail, watermarkTitle } = {},
  ) => (
    <Image
      {...image}
      className={className}
      thumbnail={thumbnail}
      watermarkTitle={watermarkTitle}
      src={thumbnail ? thumbnailPath ?? path : path}
    />
  ),
  [MediaType.VIDEO]: (
    media,
    {
      className, thumbnail, autoplay, muted, controls, onSeeking, disableAutoPause,
    } = {},
  ) => (
    <Video
      media={media}
      autoplay={autoplay}
      thumbnail={thumbnail}
      onSeeking={onSeeking}
      className={className}
      disableAutoPause={disableAutoPause}
      {...(typeof muted === 'boolean' ? { muted } : {})}
      {...(typeof controls === 'boolean' ? { controls } : {})}
    />
  ),
  [MediaType.DOCUMENT]: (
    document,
    { thumbnail, onPageChange, hideFilename } = {},
  ) => <Document document={document} thumbnail={thumbnail} onPageChange={onPageChange} hideFilename={hideFilename} />,
};

export interface FileProps extends AbstractMedia {
  className?: Argument;
  autoplay?: boolean;
  muted?: boolean;
  controls?: boolean;
  hideFilename?: boolean;
  onDelete?: (media: MediaInterface) => void;
  onClick?: (media: MediaInterface, event: React.MouseEvent) => void;
}

const File: React.FC<FileProps & Partial<MediaInterface>> = ({
  className,
  autoplay,
  muted,
  controls,
  onClick,
  onDelete,
  hideFilename,
  ...mediaProps
}) => {
  const intl = useIntl();
  const media = React.useMemo(() => new Media(mediaProps), [mediaProps]);
  const { isLoaded, isLoading, isDeleting } = media;

  const isClickable = Boolean(onClick && isLoaded);

  const handleOnClick = React.useCallback((event: React.MouseEvent) => {
    if (isClickable) {
      onClick(media, event);
    }
  }, [isClickable, media, onClick]);

  const handleOnDelete = React.useCallback((event: React.MouseEvent) => {
    event.stopPropagation();

    if (!isDeleting) {
      onDelete(media);
    }
  }, [isDeleting, media, onDelete]);

  return (
    <div
      onClick={handleOnClick}
      data-testid={`file-${media.path}`}
      className={classNames(
        'relative size-16 kiosk:size-36 flex rounded-card overflow-hidden z-0 bg-black',
        { 'cursor-pointer': isClickable },
        className,
      )}>
      {isLoading && <Spinner className="h-full" />}
      {!isLoading && !isLoaded && (
        <div className="m-auto text-center text-inverse text-xs flex flex-wrap">
          <Icon className="fb-icon--media text-2xl kiosk:text-5xl m-auto text-white" />
          <FormattedMessage id="media.thumbnailUnavailable" defaultMessage="Thumbnail unavailable" />
        </div>
      )}
      {isLoaded && DEFAULT_MEDIA_COMPONENTS[media.type](
        media,
        { className: 'w-full h-full object-cover', thumbnail: true, autoplay, muted, controls, hideFilename },
      )}
      {onDelete && (
        <Button
          type={ButtonType.ICON}
          onClick={handleOnDelete}
          aria-label={intl.formatMessage({ id: 'accessibility.button.deleteMedia', defaultMessage: 'Delete the media' })}
          className="absolute z-20 right-0 top-0"
        >
          <Icon className="fb-icon--close text-sm leading-none kiosk:text-xl p-1 kiosk:px-2 aspect-square absolute top-1 right-1 kiosk:right-3 kiosk:top-3 cursor-pointer bg-default rounded-full z-30" />
        </Button>
      )}
    </div>
  );
};

export default File;
